import { AfterViewInit, Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { CurrencyService } from 'app/services/currency.service';
import {
	ICompensationStatsResponse,
	StatsService,
} from 'app/services/stats.service';
import { ZkuService } from 'app/services/zku.service';
import * as moment from 'moment';

@Component({
	selector: 'app-chart-compensation',
	templateUrl: './chart-compensation.component.html',
	styleUrls: ['./chart-compensation.component.scss'],
})
export class ChartCompensationComponent implements OnInit, AfterViewInit {
	minDate: moment.Moment;
	maxDate: moment.Moment;

	data: any;
	options: any;
	sumCompensation: string;
	currencySymbol: string;
	isNextDisabled = true;
	isLoading = true;
	isVisible = false;
	isProfessionalZen = false;
	isProfessionalZenPartner = false;
	errorTitle: string;

	constructor(
		private statsSevice: StatsService,
		private notifications: ZkNotificationService,
		private currencyService: CurrencyService,
		private translate: TranslateService,
		private zkuService: ZkuService
	) {
		this.currencySymbol = this.currencyService.userPref.CurrencySymbol;
		this.setInitialDateRange();
		const userService = this.zkuService.getZku();

		this.isProfessionalZen = userService?.tags?.some(
			(tag) => tag['id'] === 'professional_zen'
		);

		this.isProfessionalZenPartner = userService?.tags?.some(
			(tag) => tag['id'] === 'professional_zen_partner'
		);

		this.isVisible = !this.isProfessionalZen && !this.isProfessionalZenPartner;
	}

	ngOnInit() {
		this.translate
			.get(['MESSAGES.COMPENSATION_CHART_ERROR'])
			.subscribe((res) => {
				this.errorTitle = res['MESSAGES.COMPENSATION_CHART_ERROR'];
			});

		this.options = {
			aspectRatio: 4,
			plugins: {
				legend: {
					display: false,
				},
			},
			layout: {
				padding: {
					top: 5,
				},
			},
			scales: {
				yAxes: [
					{
						display: false,
					},
				],
				xAxes: [
					{
						display: false,
					},
				],
			},
		};
	}

	loadCompensationStats() {
		this.isLoading = true;
		const sub = this.statsSevice
			.getCompensationStats(this.minDate, this.maxDate)
			.subscribe(
				(response: ICompensationStatsResponse) => {
					const dates = {};
					const labels: string[] = [];
					const data: number[] = [];
					const now = this.getCurrentDate().startOf('day');
					const cursor = this.minDate.clone();
					let cumulative = 0;

					if (response.stats && response.stats.length) {
						response.stats.forEach((compensation) => {
							dates[moment(compensation.day).format('YYYY-MM-DD')] =
								compensation.totalCompensation;
						});
					}

					while (cursor.isSameOrBefore(this.maxDate)) {
						const key = cursor.format('YYYY-MM-DD');

						if (cursor.isAfter(now)) {
							data.push(0);
						} else if (dates[key]) {
							cumulative += dates[key];
							data.push(cumulative);
						} else {
							data.push(cumulative);
						}

						cursor.add(1, 'day');
						labels.push(moment(key).format('MMM DD'));
					}

					this.isVisible =
						this.isVisible && Boolean(response.hadRevenueAllTime);

					this.sumCompensation =
						this.currencyService.formatWithDecimals(cumulative);

					this.data = {
						labels,
						datasets: [
							{
								data,
								fill: true,
								backgroundColor: 'RGBA(5,161,250,0.8)',
								pointBackgroundColor: '#fff',
								pointBorderColor: '#05A1FA',
								pointBorderWidth: 2,
							},
						],
					};

					sub.unsubscribe();
					this.isLoading = false;
				},
				() => {
					this.isLoading = false;
					this.notifications.showError(this.errorTitle);
				}
			);
	}

	previousMonth() {
		this.minDate.subtract(1, 'month').set('date', 24);
		this.maxDate.subtract(1, 'month').set('date', 23);
		this.isNextDisabled = false;
		this.loadCompensationStats();
	}

	nextMonth() {
		const now = this.getCurrentDate();
		if (now <= this.maxDate) {
			return;
		}
		this.minDate.add(1, 'month').set('date', 24);
		this.maxDate.add(1, 'month').set('date', 23);
		this.loadCompensationStats();
	}

	ngAfterViewInit() {
		this.loadCompensationStats();
	}

	setInitialDateRange() {
		const now = this.getCurrentDate();
		this.minDate = this.getCurrentDate()
			.startOf('day')
			.subtract(1, 'month')
			.set('date', 24);
		this.maxDate = this.getCurrentDate().endOf('day').set('date', 23);
		if (now.date() > 23) {
			this.minDate = this.getCurrentDate().startOf('day').set('date', 24);
			this.maxDate = this.getCurrentDate()
				.endOf('day')
				.add(1, 'month')
				.set('date', 23);
		}
	}

	getReportHeaderTranslated() {
		const translatePath = 'CALENDAR.MONTHS';
		const startMonth = this.translate.instant(
			`${translatePath}.${this.minDate.format('MMM').toUpperCase()}.ABB`
		);
		const endMonth = this.translate.instant(
			`${translatePath}.${this.maxDate.format('MMM').toUpperCase()}.ABB`
		);
		return `${startMonth}/${this.minDate.format(
			'DD'
		)} - ${endMonth}/${this.maxDate.format('DD')}`;
	}

	getCurrentDate(): moment.Moment {
		return moment();
	}
}
